import { buildQueryString } from "@workspace/lib/url";
import { apiClient } from "~/lib/client";
import { handleApiError, handleResponse } from "~/utils/response";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";

import { PatientFilterOptions, PatientLogsResponse, PatientMedicalPlan, PatientMedication, PatientNotes, PatientPaymentHistory, PatientQuestionnaire, PatientTimeline, GetPatientSummaryResponse, PatientSessions, PatientMedicalHistory, PatientChatHistory, PatientDiagnosisResponse, PatientRefills, UrgentPatientMedication } from "../types";
import { patientLogsKeys } from "./keys";
import { isToday } from "date-fns";
import { getApiProgram } from "~/lib/utils";


export const patientsQueryOptions = (opts: PatientFilterOptions) =>
    queryOptions({
        queryKey: patientLogsKeys.list(opts),
        retry: false,
        queryFn: async () => {
            const { limit, skip, search, status, program, payment_status, status_filter, verification_status } = opts;

            const response = await apiClient(`/admin/patients${buildQueryString({
                skip: skip,
                limit: limit,
                search: search,
                program: program,
                filter: status !== 'all' ? status : '',
                payment_status: payment_status,
                status_filter: status_filter,
                verification_status: verification_status,
            })}`);

            const result = await handleResponse<PatientLogsResponse>(response);

            if (!result.success) {
                throw new Error(result.error);
            }

            return result.data;
        },
        placeholderData: keepPreviousData,
        select(data) {
            const patients = data?.patients?.map(patient => {
                const initials = patient.full_name
                    ? patient.full_name.split(' ').map(name => name[0]).join('')
                    : '';
                return {
                    initials,
                    userId: patient.user_id,
                    name: patient.full_name || '',
                    glp1_guide_accepted: patient.glp1_guide_accepted,
                    identity_verified: patient.identity_verified.verified,
                    age: patient.age,
                    email: patient.email,
                    zip_code: patient.zip_code,
                    address: patient.address,
                    gender: patient.gender,
                    consultation: patient.consultation,
                    medicalPlan: patient.medical_plan,
                    isActive: patient.is_active,
                    isNew: isToday(new Date(patient.registration_date)),
                    initialWeight: patient?.initial_weight || "0",
                    goalWeight: patient?.target_weight || "0",
                    registrationDate: patient?.registration_date || "",
                    program: 'Weight Loss Program',
                    status: patient.is_active ? 'Active' : 'Inactive',
                    dateOfBirth: patient?.date_of_birth || "",
                    phoneNumber: patient?.phone_number || "",
                    patient_sessions_state: patient.patient_sessions_state || "",
                    state: patient?.state || "",
                    city: patient?.city || "",
                    user_dosespot_id: patient.user_dosespot_id || "",
                    urgent_care_onboarded: patient.urgent_care_onboarded,
                    pharmacy: patient?.urgent_care_pharmacy,
                    weight_loss_pharmacy: patient?.weight_loss_pharmacy,
                    supplementary_health_assessment: patient?.supplementary_health_assessment,
                };
            }) || [];

            return {
                patients,
                hasMore: !!(data?.total > (opts?.limit ?? 10)),
                total: data?.total || 0,
            };
        }
    });

export const patientDetailsQueryOptions = (id: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.detail(id),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${id}?program=${apiProgram}`);
                const result = await handleResponse<GetPatientSummaryResponse>(response)
                if (!result.success) {
                    throw new Error(result.error)
                }
                return result.data
            } catch (error) {
                console.error("Error fetching weight summary by patient ID:", error);
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            const initials = data?.patient_summary?.full_name
                ? data?.patient_summary.full_name.split(' ').map(name => name[0]).join('')
                : '';
            return {
                userId: data?.patient_summary?.user_id || '',
                initials,
                isActive: data?.patient_summary?.is_active || false,
                name: data?.patient_summary?.full_name || "",
                age: data?.patient_summary?.age || 0,
                email: data?.patient_summary?.email || "",
                glp1_guide_accepted: data?.patient_summary?.glp1_guide_accepted,
                identity_verified: data?.patient_summary?.identity_verified.verified,
                address: data?.patient_summary?.address || "",
                gender: data.patient_summary?.gender || '',
                initialWeight: data?.patient_summary?.initial_weight || "0",
                goalWeight: data?.patient_summary?.target_weight || "0",
                registrationDate: data?.patient_summary?.registration_date || "",
                isNew: isToday(new Date(data?.patient_summary?.registration_date ?? new Date())),
                program: 'Weight Loss Program',
                status: 'Active',
                dateOfBirth: data?.patient_summary?.date_of_birth || '',
                phoneNumber: data?.patient_summary?.phone_number || '',
                state: data?.patient_summary?.state || '',
                city: data?.patient_summary?.city || '',
                user_dosespot_id: data?.patient_summary?.user_dosespot_id || "",
                // Added missing fields from patientsQueryOptions
                zip_code: data?.patient_summary?.zip_code || '',
                medicalPlan: data?.patient_summary?.medical_plan,
                patient_sessions_state: data?.patient_summary?.patient_sessions_state || "",
                urgent_care_onboarded: data?.patient_summary?.urgent_care_onboarded,
                pharmacy: data?.patient_summary?.urgent_care_pharmacy,
                weight_loss_pharmacy: data?.patient_summary?.weight_loss_pharmacy,
                supplementary_health_assessment: data?.patient_summary?.supplementary_health_assessment,
                consultation: {
                    consultation_id: data?.patient_summary?.consultation?.consultation_id || '',
                    user_id: data?.patient_summary?.consultation?.user_id || '',
                    program: data?.patient_summary?.consultation?.program || '',
                    status: data?.patient_summary?.consultation?.status || '',
                    cost: data?.patient_summary?.consultation?.cost || 0,
                    payment_status: data?.patient_summary?.consultation?.payment_status || '',
                    request_datetime: data?.patient_summary?.consultation?.request_datetime || '',
                    created_at: data?.patient_summary?.consultation?.created_at || 0,
                    is_first: data?.patient_summary?.consultation?.is_first || false,
                    plan_id: data?.patient_summary?.consultation?.plan_id || ''
                },
            }
        }
    })
}

export const patientTimelineQueryOptions = (patientId: string) => {
    return queryOptions({
        queryKey: patientLogsKeys.timeline(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/timeline`);
                const result = await handleResponse<PatientTimeline>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return {
                status: data?.status || '',
                message: data?.message || '',
                timeline: data?.timeline?.map(timeline => {
                    return {
                        event_type: timeline.event_type || "Enrollment in Weight Loss Program",
                        encounter_datetime: timeline.encounter_datetime,
                        description: timeline.description || "Patient completed initial questionnaire and enrolled in the 6-month weight loss program.",
                        id: timeline.id
                    };
                }) || [],
                patient_summary: {
                    full_name: data?.patient_summary?.full_name || '',
                    age: data?.patient_summary?.age || 0,
                    gender: data?.patient_summary?.gender || ''
                }
            };
        }
    });
};

export const patientRefillsQueryOptions = (patientId: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.refills(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/refills?program=${apiProgram}&patient_id=${patientId}`);
                const result = await handleResponse<PatientRefills>(response);
                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        }
    });
};

export const patientSessionQueryOption = (patientId: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.sessions(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/sessions?program=${apiProgram}`);
                const result = await handleResponse<PatientSessions>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return {
                status: data?.status || '',
                message: data?.message || '',
                sessions: data?.sessions?.map(session => ({
                    user_id: session.user_id || '',
                    session_id: session.session_id || '',
                    session_number: session.session_number || 0,
                    session_type: session.session_type || '',
                    start_datetime: session.start_datetime || '',
                    thread_id: session.thread_id || '',
                    agent_id: session.agent_id || '',
                    status: session.status || '',
                    onboard_session: session.onboard_session || '',
                    alfie_session: session.alfie_session || '',
                    payment_status: session.payment_status || '',
                    treatment_completed: session.treatment_completed || false,
                    is_red_flagged: session.is_red_flagged || false,
                    created_at: session.created_at || 0,
                    intake_questions: session.intake_questions || {},
                    red_flag_questions: session.red_flag_questions || {},
                    consultation_id: session.consultation_id || '',
                    reported_symptom: session.reported_symptom || '',
                    state: session.state,
                    tasks: session.tasks?.map(task => ({
                        task_id: task.task_id || '',
                        user_id: task.user_id || '',
                        type: task.type || '',
                        title: task.title || '',
                        priority: task.priority || false,
                        when: task.when || '',
                        payment_required: task.payment_required || false,
                        created_on: task.created_on || '',
                        completed_on: task.completed_on || '',
                        completed: task.completed || false,
                        status: task.status || '',
                        current_month: task.current_month || 0,
                        user_month: task.user_month || 0,
                        session_id: task.session_id || '',
                        program: task.program || '',
                        consultation_id: task.consultation_id || '',
                    })) || [],
                    diagnosis: session.diagnosis || [],
                    encounter: session.encounter
                })) || []
            };
        }
    });
};

export const patientMedicationQueryOptions = (patientId: string, program: string, filter: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: [...patientLogsKeys.medication(patientId), program, filter], // ← Add program and filter
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/medication?program=${apiProgram}&filter=${filter}`);
                const result = await handleResponse<PatientMedication>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        }
    });
};

export const urgentPatientMedicationQueryOptions = (patientId: string, sessionId: string, program: string, filter: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: [...patientLogsKeys.medication(patientId), 'urgent', sessionId, program, filter],
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/sessions/${sessionId}/medication?program=${apiProgram}&filter=${filter}`);
                const result = await handleResponse<UrgentPatientMedication>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
    });
}

export const patientMedicalPlanQueryOptions = (patientId: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.medicalPlan(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/medical-overview?program=${apiProgram}`);
                const result = await handleResponse<PatientMedicalPlan>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return {
                status: data?.status || '',
                message: data?.message || '',
                patient_summary: {
                    full_name: data?.patient_summary?.full_name || '',
                    age: data?.patient_summary?.age || 0,
                    gender: data?.patient_summary?.gender || ''
                },
                program_details: {
                    program: data?.program_details?.program || '',
                    start_date: data?.program_details?.start_date || '',
                    end_date: data?.program_details?.end_date || '',
                    plan: {
                        program: data?.program_details?.plan?.program || '',
                        session_id: data?.program_details?.plan?.session_id || null,
                        medication: {
                            name: data?.program_details?.plan?.medication?.name || '',
                            dosage: data?.program_details?.plan?.medication?.dosage || '',
                            substitute: data?.program_details?.plan?.medication?.substitute || false,
                        },
                        vitamin: {
                            name: data?.program_details?.plan?.vitamin?.name || ''
                        },
                        consultation: {
                            name: data?.program_details?.plan?.consultation?.name || '',
                            cost: data?.program_details?.plan?.consultation?.cost || ''
                        },
                        success_url: data?.program_details?.plan?.success_url || '',
                        cancel_url: data?.program_details?.plan?.cancel_url || '',
                        plan_id: data?.program_details?.plan?.plan_id || '',
                        user_id: data?.program_details?.plan?.user_id || '',
                        payment_status: data?.program_details?.plan?.payment_status || '',
                        supplementary_questionnaire: data?.program_details?.plan?.supplementary_questionnaire || [],
                        created_datetime: data?.program_details?.plan?.created_datetime || '',
                        created_at: data?.program_details?.plan?.created_at || ''
                    }
                },
                appointment_schedule: {
                    initial_consultation: {
                        status: data?.appointment_schedule?.initial_consultation?.status || 'Completed',
                        date: data?.appointment_schedule?.initial_consultation?.date || '3/20/2025'
                    },
                    follow_ups: data?.appointment_schedule?.follow_ups || []
                },
                medication_plan: data?.medication_plan || [
                    {
                        medication: '',
                        price: '',
                        prescribed_date: '',
                        refills: '',
                        status: '',
                        vitamin: {
                            name: ""
                        },
                    }
                ],
                conditions_and_allergies: {
                    allergies: data?.conditions_and_allergies?.allergies || [
                        {
                            allergy: 'Peanuts',
                            status: 'Severe'
                        },
                        {
                            allergy: 'Pollen',
                            status: 'Mild'
                        },
                        {
                            allergy: 'Dust Mites',
                            status: 'Mild'
                        }
                    ],
                    health_conditions: data?.conditions_and_allergies?.health_conditions || [
                        {
                            condition: 'High Blood Pressure',
                            status: 'Severe'
                        },
                        {
                            condition: 'Type 2 Diabetes',
                            status: 'Mild'
                        },
                        {
                            condition: 'Asthma',
                            status: 'Mild'
                        }
                    ]
                }
            };
        }
    });
};

export const patientQuestionnaireQueryOptions = (patientId: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.questionnaire(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/questionnaire?program=${apiProgram}`);
                const result = await handleResponse<PatientQuestionnaire>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;

            } catch (error) {
                handleApiError(error);
                throw error;
            }
        }
    });
};

export const patientPaymentHistoryQueryOptions = (patientId: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.paymentHistory(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/payment-history?program=${apiProgram}`);
                const result = await handleResponse<PatientPaymentHistory>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;

            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return {
                ...data,
                payments: data?.payments?.map(payment => ({
                    ...payment,
                    date: payment.payment_date || '',
                    status: payment.status || ''
                })) || []
            };
        }
    });
};

export const patientNotesQueryOptions = (patientId: string) => {
    return queryOptions({
        queryKey: patientLogsKeys.notes(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/notes`);
                const result = await handleResponse<PatientNotes>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;

            } catch (error) {
                handleApiError(error);
                throw error;
            }
        }
    });
};

export const patientMedicalHistoryQueryOptions = (patientId: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.medicalHistory(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/medical-history?program=${apiProgram}`);
                const result = await handleResponse<PatientMedicalHistory>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return {
                status: data?.status || '',
                message: data?.message || '',
                allergies: data?.allergies || [],
                conditions: data?.conditions || []
            };
        }
    });
};

export const patientSessionMedicalHistoryQueryOptions = (patientId: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.medicalHistory(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/medical-history?program=${apiProgram}`);
                const result = await handleResponse<PatientMedicalHistory>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return {
                status: data?.status || '',
                important_health_data: data?.important_health_data,
                message: data?.message || '',
                allergies: data?.allergies || [],
                conditions: data?.conditions || []
            };
        }
    });
};

export const patientChatHistoryQueryOptions = (patientId: string, sessionId: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.medicalHistory(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/sessions/${sessionId}/chat-history?program=${apiProgram}`);
                const result = await handleResponse<PatientChatHistory>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
    });
};

export const patientDiagnosisQueryOptions = (patientId: string, sessionId: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.medicalHistory(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/sessions/${sessionId}/diagnosis?program=${apiProgram}`);
                const result = await handleResponse<PatientDiagnosisResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return {
                status: data?.status || '',
                message: data?.message || '',
                session: data.session ? {
                    user_id: data.session.user_id || '',
                    session_id: data.session.session_id || '',
                    session_type: data.session.session_type || '',
                    entry_type: data.session.entry_type || '',
                    start_datetime: data.session.start_datetime || '',
                    status: data.session.status || '',
                    payment_status: data.session.payment_status || '',
                    treatment_completed: data.session.treatment_completed || false,
                    created_at: data.session.created_at || 0,
                    thread_id: data.session.thread_id || '',
                    agent_id: data.session.agent_id || '',
                    alfie_session: data.session.alfie_session || '',
                    reported_symptom: data.session.reported_symptom ?? null,
                    intake_questions: data.session.intake_questions ?? undefined,
                    red_flag_questions: data.session.red_flag_questions ?? undefined,
                    final_questions: data.session.final_questions ?? null,
                    symptoms: data.session.symptoms ?? null
                } : null,
                diagnosis: data?.diagnosis?.map(diagnosis => ({
                    disease_name: diagnosis.disease_name || '',
                    image_prompt: diagnosis.image_prompt || '',
                    overview: diagnosis.overview || '',
                    symptoms: diagnosis.symptoms || [],
                    red_flags: diagnosis.red_flags || [],
                    diagnosis: diagnosis.diagnosis || [],
                    causes: diagnosis.causes || [],
                    prevention: diagnosis.prevention || [],
                    treatment_page: diagnosis.treatment_page ? {
                        red_flag_questions: diagnosis.treatment_page.red_flag_questions || [],
                        medications: diagnosis.treatment_page.medications?.map(med => ({
                            category: med.category || '',
                            medication: med.medication || '',
                            quantity: med.quantity || '',
                            instructions: med.instructions || [],
                            is_primary: med.is_primary || false,
                            is_secondary: med.is_secondary || false,
                            is_extra: med.is_extra || false
                        })) || [],
                        additional_question: diagnosis.treatment_page.additional_question || null
                    } : null,
                    general_questions: diagnosis.general_questions || [],
                    disease_id: diagnosis.disease_id || '',
                    affected_area: diagnosis.affected_area || [],
                    image_urls: diagnosis.image_urls || []
                })) || [],
                actions: data?.actions || []
            };
        }
    });
};

export const patientAppointmentsQueryOptions = (patientId: string, sessionId: string, program: string) => {
    const apiProgram = getApiProgram(program)
    return queryOptions({
        queryKey: patientLogsKeys.medicalHistory(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${patientId}/sessions/${sessionId}/appointments?program=${apiProgram}`);
                const result = await handleResponse<PatientMedicalHistory>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return {
                status: data?.status || '',
                message: data?.message || '',
                allergies: data?.allergies || [],
                conditions: data?.conditions || []
            };
        }
    });
};

export interface ICD10SearchResult {
    code: string;
    description: string;
    category: string;
}

export interface ICD10SearchResponse {
    status: string;
    message: string | null;
    results: ICD10SearchResult[];
}

export const icd10SearchQueryOptions = (searchTerm: string) => {
    return queryOptions({
        queryKey: patientLogsKeys.icd10Search(searchTerm),
        retry: false,
        queryFn: async () => {
            try {
                if (!searchTerm || searchTerm.trim().length < 2) {
                    return [];
                }

                const searchParams = new URLSearchParams({
                    search_term: searchTerm.trim()
                });
                const response = await apiClient(`/admin/tools/search-cd10?${searchParams.toString()}`);
                const result = await handleResponse<ICD10SearchResponse>(response);

                if (!result.success) {
                    throw new Error(result.error || 'Failed to search ICD-10 codes');
                }

                if (result.data.status !== "success") {
                    throw new Error(result.data.message || 'Failed to search ICD-10 codes');
                }

                return result.data.results || [];
            } catch (error) {
                handleApiError(error);
                // Return empty array on error to prevent breaking the UI
                return [];
            }
        },
        enabled: !!searchTerm && searchTerm.trim().length >= 2,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
};

export interface VerificationResponse {
    status: string;
    message: string | null;
    verification: {
        user_id: string;
        auto_rejection_reason: string | null;
        created_at: string;
        id_type: string;
        id_url: string;
        manual_rejection_reason: string | null;
        method: string;
        photo_url: string;
        rejected_on: string | null;
        status: string;
        submitted_on: string;
        updated_at: string;
        verified: boolean;
        verified_on: string | null;
    };
}

export const patientVerificationQueryOptions = (patientId: string) => {
    return queryOptions({
        queryKey: patientLogsKeys.verification(patientId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/verifications/${patientId}`);
                const result = await handleResponse<VerificationResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        }
    });
};